import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DiaConfigCard, { DiaChip } from "@/components/DiaConfigCard";
import ResumenSemanal from "@/components/ResumenSemanal";

const API = "/api";

function getMondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function toISODate(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(isoDate, days) {
  const d = new Date(isoDate + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return toISODate(d);
}

function buildDefaultDias(semanaInicio) {
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(semanaInicio + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + i);
    return {
      dia_semana: i,
      fecha: toISODate(d),
      activo: i < 5,
      es_feriado: false,
      feriado_motivo: "",
      franjas_horarias: [],
      coberturas_habilitadas: ["Particular", "Urgencias"],
    };
  });
}

function formatSemanaLabel(semanaInicio) {
  const monday = new Date(semanaInicio + "T00:00:00Z");
  const saturday = new Date(semanaInicio + "T00:00:00Z");
  saturday.setUTCDate(monday.getUTCDate() + 5);

  const fmt = (d) =>
    d.toLocaleDateString("es-AR", { day: "numeric", month: "short", timeZone: "UTC" });

  return `${fmt(monday)} – ${fmt(saturday)}`;
}

export default function PlanificacionPage() {
  const { getAuthHeader } = useAuth();

  const [semanaInicio, setSemanaInicio] = useState(() =>
    toISODate(getMondayOf(new Date()))
  );
  const [dias, setDias] = useState(() => buildDefaultDias(toISODate(getMondayOf(new Date()))));
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSemana = useCallback(
    async (semana) => {
      setLoading(true);
      try {
        const [disponResp, feriadosResp] = await Promise.all([
          axios.get(`${API}/disponibilidad?semana=${semana}`, {
            headers: getAuthHeader(),
          }),
          axios.get(`${API}/feriados?anio=${semana.slice(0, 4)}`, {
            headers: getAuthHeader(),
          }),
        ]);

        const disponData = disponResp.data;
        const feriadosData = feriadosResp.data;

        const feriadosByFecha = {};
        for (const f of feriadosData) {
          feriadosByFecha[f.fecha] = f.motivo;
        }

        if (disponData.length === 6) {
          setDias(
            disponData.map((d) => ({
              dia_semana: d.dia_semana,
              fecha: d.fecha,
              activo: d.activo,
              es_feriado: d.es_feriado,
              feriado_motivo: d.feriado_motivo || "",
              franjas_horarias: d.franjas_horarias || [],
              coberturas_habilitadas: d.coberturas_habilitadas || [],
            }))
          );
        } else {
          const defaults = buildDefaultDias(semana);
          setDias(
            defaults.map((d) =>
              feriadosByFecha[d.fecha]
                ? { ...d, activo: false, es_feriado: true, feriado_motivo: feriadosByFecha[d.fecha] }
                : d
            )
          );
        }
      } catch {
        toast.error("Error al cargar la disponibilidad");
        setDias(buildDefaultDias(semana));
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeader]
  );

  useEffect(() => {
    loadSemana(semanaInicio);
  }, [semanaInicio, loadSemana]);

  const goToPrevSemana = () => {
    setSemanaInicio((s) => addDays(s, -7));
    setSelectedIdx(0);
  };

  const goToNextSemana = () => {
    setSemanaInicio((s) => addDays(s, 7));
    setSelectedIdx(0);
  };

  const updateDia = (idx, newDia) => {
    setDias((prev) => prev.map((d, i) => (i === idx ? newDia : d)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/disponibilidad`,
        { semana_inicio: semanaInicio, dias },
        { headers: getAuthHeader() }
      );
      toast.success("Semana guardada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Planificación semanal</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Configurá tu disponibilidad para la semana
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="btn-primary flex items-center gap-2 flex-shrink-0"
        >
          <Save size={15} />
          {saving ? "Guardando..." : "Guardar semana"}
        </button>
      </div>

      {/* Week navigator */}
      <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 px-5 py-3">
        <button
          onClick={goToPrevSemana}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
          aria-label="Semana anterior"
        >
          <ChevronLeft size={18} />
        </button>
        <p className="font-semibold text-slate-800 text-sm">
          {formatSemanaLabel(semanaInicio)}
        </p>
        <button
          onClick={goToNextSemana}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
          aria-label="Semana siguiente"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-6 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
          <div className="h-48 bg-slate-100 rounded-2xl animate-pulse" />
        </div>
      ) : (
        <>
          {/* Day chips */}
          <div className="grid grid-cols-6 gap-2">
            {dias.map((dia, idx) => (
              <DiaChip
                key={dia.dia_semana}
                dia={dia}
                isSelected={selectedIdx === idx}
                onSelect={() => setSelectedIdx(idx)}
              />
            ))}
          </div>

          {/* Config panel for selected day */}
          {dias[selectedIdx] && (
            <DiaConfigCard
              dia={dias[selectedIdx]}
              onChange={(newDia) => updateDia(selectedIdx, newDia)}
            />
          )}

          {/* Weekly summary */}
          <ResumenSemanal dias={dias} />
        </>
      )}
    </div>
  );
}
